import { useCallback, useEffect, useState } from 'react';
import { procurementApi, type PurchaseRequestResponse } from '@/api/procurement.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { StoreSelect } from '@/components/selects';
import {
  createProductLineDraft,
  parseProductLines,
  ProductLineItemsEditor,
  type ProductLineDraft,
} from '@/components/forms/ProductLineItemsEditor';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { Textarea } from '@/components/ui/Textarea';
import { TablePagination } from '@/components/ui/TablePagination';
import { usePagination } from '@/hooks/usePagination';
import { useAppSelector } from '@/hooks/useAppSelector';
import { usePermissions } from '@/hooks/usePermissions';
import { useProductOptions } from '@/hooks/useProductOptions';
import { useStoreOptions } from '@/hooks/useStoreOptions';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

export function PurchaseRequestsPage() {
  const { hasAny } = usePermissions();
  const canCreate = hasAny('PROCUREMENT_REQUEST_CREATE', 'PROCUREMENT_MANAGE');
  const canApprove = hasAny('PROCUREMENT_REQUEST_APPROVE', 'PROCUREMENT_MANAGE');
  const selectedStore = useAppSelector((s) => s.app.selectedStore);
  const { getLabel: getStoreLabel } = useStoreOptions();
  const { getLabel: getProductLabel } = useProductOptions();
  const pagination = usePagination();
  const [items, setItems] = useState<PurchaseRequestResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [storeId, setStoreId] = useState('');
  const [requiredDate, setRequiredDate] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<ProductLineDraft[]>([
    createProductLineDraft({ quantity: '10', unitPriceEstimate: '50' }),
  ]);

  useEffect(() => {
    if (selectedStore && !storeId) {
      setStoreId(String(selectedStore.id));
    }
  }, [selectedStore, storeId]);

  const load = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await procurementApi.listPurchaseRequests({
        page: pagination.page,
        size: pagination.size,
      });
      setItems(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load purchase requests');
    } finally {
      setListLoading(false);
    }
  }, [pagination.page, pagination.size, pagination]);

  useEffect(() => {
    void load();
  }, [load]);

  const resetForm = () => {
    setRequiredDate('');
    setNotes('');
    setLines([createProductLineDraft({ quantity: '10', unitPriceEstimate: '50' })]);
  };

  const createRequest = async () => {
    const resolvedStoreId = storeId ? Number(storeId) : selectedStore?.id;
    if (!resolvedStoreId) {
      notify.error('Select a store first');
      return;
    }
    const itemsPayload = parseProductLines(lines, 'estimate');
    if (itemsPayload.length === 0) {
      notify.error('Add at least one product line with quantity');
      return;
    }
    setLoading(true);
    try {
      const pr = await procurementApi.createPurchaseRequest({
        storeId: resolvedStoreId,
        requiredDate: requiredDate || undefined,
        notes: notes.trim() || undefined,
        items: itemsPayload,
      });
      const submitted = await procurementApi.submitPurchaseRequest(pr.id);
      setItems((prev) => [submitted, ...prev.filter((x) => x.id !== submitted.id)]);
      notify.success(`Request ${submitted.requestNumber} submitted`);
      resetForm();
    } catch (err) {
      showApiError(err, 'Create purchase request failed');
    } finally {
      setLoading(false);
    }
  };

  const approve = async (id: number) => {
    setBusyId(id);
    try {
      const updated = await procurementApi.approvePurchaseRequest(id, 'APPROVE');
      setItems((prev) => prev.map((x) => (x.id === id ? updated : x)));
      notify.success('Request approved');
    } catch (err) {
      showApiError(err, 'Approval failed');
    } finally {
      setBusyId(null);
    }
  };

  const cancel = async (id: number) => {
    setBusyId(id);
    try {
      const updated = await procurementApi.cancelPurchaseRequest(id, 'Cancelled by user');
      setItems((prev) => prev.map((x) => (x.id === id ? updated : x)));
      notify.success('Request cancelled');
    } catch (err) {
      showApiError(err, 'Cancel failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <PageHeader
        title="Purchase requests"
        description="Internal product requests before RFQ and purchase order"
        breadcrumbs={[
          { label: 'Procurement', to: '/procurement' },
          { label: 'Purchase requests' },
        ]}
        actions={
          canCreate ? (
            <Button loading={loading} onClick={() => void createRequest()}>Submit request</Button>
          ) : undefined
        }
      />

      {canCreate && (
        <section className="card mb-4 space-y-4 p-4">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">New purchase request</h2>
          <div className="grid gap-3 md:grid-cols-2">
            <StoreSelect label="Store *" value={storeId} onChange={setStoreId} required />
            <DatePicker label="Required by date" value={requiredDate} onChange={setRequiredDate} />
            <div className="md:col-span-2">
              <Textarea
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Reason or delivery instructions"
                rows={2}
              />
            </div>
          </div>
          <ProductLineItemsEditor lines={lines} onChange={setLines} priceMode="estimate" />
        </section>
      )}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left dark:border-slate-700">
              <th className="p-3">Number</th>
              <th className="p-3">Store</th>
              <th className="p-3">Status</th>
              <th className="p-3">Amount</th>
              <th className="p-3">Lines</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-4 text-slate-500">
                  No requests yet. Create one using the form above.
                </td>
              </tr>
            ) : (
              items.map((pr) => (
                <tr key={pr.id} className="border-b border-slate-100 dark:border-slate-800">
                  <td className="p-3 font-medium">{pr.requestNumber}</td>
                  <td className="p-3">{getStoreLabel(pr.storeId, true)}</td>
                  <td className="p-3">{pr.requestStatus}</td>
                  <td className="p-3">{pr.totalAmount}</td>
                  <td className="p-3 text-xs text-slate-600 dark:text-slate-400">
                    {pr.items?.map((line) => `${getProductLabel(line.productId, true)} × ${line.quantity}`).join(', ') || '—'}
                  </td>
                  <td className="p-3 flex flex-wrap gap-2">
                    {pr.requestStatus === 'PENDING_APPROVAL' && canApprove && (
                      <Button loading={busyId === pr.id} onClick={() => void approve(pr.id)}>
                        Approve
                      </Button>
                    )}
                    {canCreate && !['CANCELLED', 'CLOSED', 'RFQ_CREATED'].includes(pr.requestStatus) && (
                      <Button variant="secondary" loading={busyId === pr.id} onClick={() => void cancel(pr.id)}>
                        Cancel
                      </Button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <TablePagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          totalElements={pagination.totalElements}
          onPageChange={pagination.setPage}
          loading={listLoading}
          hasRows={items.length > 0}
        />
      </div>
    </div>
  );
}
